import { useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChatContextType, extractBrandInsightsIds } from '@trinity/chat';
import type { Segments } from '@trinity/chat/api/interfaces/projects';
import { ActiveChatSessionContext } from '../Chat/context';
import { SegmentChatContext } from './SegmentChat.Context';

type Props = {
  fetchProjectSegments: (
    data: Segments.GetSegmentsByProjectId.Request
  ) => Promise<Segments.GetSegmentsByProjectId.Response>;
};

/**
 * Fetches all project segments and populates `segmentIconsCache` in `SegmentChatContext`, so the
 * segment avatar is available when the user picks a segment to chat with (the `SegmentChatNeeded`
 * state from chat-api carries `iconUrl: null`). Only runs for segmentation projects.
 */
export const SegmentIconCacheLoader = ({ fetchProjectSegments }: Props) => {
  const chat = useContext(ActiveChatSessionContext);
  const { setSegmentIconsCache } = useContext(SegmentChatContext);

  const isSegmentationProject = useMemo(() => {
    const contextType = chat?.context?.type;
    const metadata = chat?.context?.metadata as Record<string, unknown> | undefined;
    return (
      (contextType === ChatContextType.Project || contextType === ChatContextType.Agent) &&
      !!metadata?.isSegmentationProject
    );
  }, [chat]);

  const projectId = useMemo(() => {
    if (!chat?.context) return null;
    const { projectId } = extractBrandInsightsIds(chat.context);
    return projectId ?? null;
  }, [chat]);

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- fetchProjectSegments identity isn't part of the query's cache key
  const { data } = useQuery({
    queryKey: ['get:projects/segments', projectId],
    queryFn: () => {
      if (projectId === null) {
        throw new Error('projectId is required to fetch project segments');
      }
      return fetchProjectSegments({ projectId });
    },
    enabled: isSegmentationProject && projectId !== null,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!data?.items?.length) return;
    const cache: Record<string, string | undefined> = {};
    for (const { segment } of data.items) {
      if (segment.identifier && segment.iconUrl) {
        cache[segment.identifier] = segment.iconUrl;
      }
    }
    setSegmentIconsCache(cache);
  }, [data, setSegmentIconsCache]);

  return null;
};
